import * as XLSX from "xlsx"

/**
 * Convert an array of plain objects into an XLSX worksheet and trigger a
 * browser download. The object keys become the column headers (in insertion
 * order), matching the convention used across the other export utils.
 *
 * @param {Array<Object>} data     Rows to export (keys = column headers)
 * @param {string}        fileName File name (without the .xlsx extension)
 * @param {string}        sheetName Optional worksheet/tab name
 */
const exportToExcel = (data, fileName, sheetName = "Sheet1") => {
	// Convert the data to a worksheet
	const worksheet = XLSX.utils.json_to_sheet(data)

	// Create a new workbook
	const workbook = XLSX.utils.book_new()

	// Append the worksheet to the workbook
	XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)

	// Generate a binary string and trigger a download
	XLSX.writeFile(workbook, `${fileName}.xlsx`)
}

/**
 * Build an XLSX workbook with one tab per sheet definition.
 *
 * @param {Array<{name: string, rows: Array<Object>, headers?: string[]}>} sheets
 *        Each entry becomes a tab. Object keys of `rows` are the column headers;
 *        when `rows` is empty, `headers` (if given) still writes a header row so
 *        the tab isn't blank.
 */
const buildWorkbook = (sheets) => {
	const workbook = XLSX.utils.book_new()

	sheets.forEach(({ name, rows, headers }) => {
		const worksheet = rows && rows.length > 0 ? XLSX.utils.json_to_sheet(rows) : XLSX.utils.aoa_to_sheet(headers ? [headers] : [])

		// Sheet/tab names are capped at 31 chars by the spreadsheet format
		XLSX.utils.book_append_sheet(workbook, worksheet, name.slice(0, 31))
	})

	return workbook
}

/**
 * Export several worksheets into a single XLSX workbook (one tab per sheet) and
 * trigger a browser download.
 *
 * @param {Array<{name: string, rows: Array<Object>, headers?: string[]}>} sheets See buildWorkbook
 * @param {string} fileName File name (without the .xlsx extension)
 */
export const exportSheetsToExcel = (sheets, fileName) => {
	XLSX.writeFile(buildWorkbook(sheets), `${fileName}.xlsx`)
}

/**
 * The XLSX file for several worksheets as bytes, for writing to disk rather
 * than downloading.
 *
 * @param {Array<{name: string, rows: Array<Object>, headers?: string[]}>} sheets See buildWorkbook
 * @returns {ArrayBuffer}
 */
export const sheetsToExcelBytes = (sheets) => XLSX.write(buildWorkbook(sheets), { bookType: "xlsx", type: "array" })

export default exportToExcel
